import { extendTailwindMerge } from 'tailwind-merge';

/**
 * @fileoverview Utilities for merging and deduplicating Tailwind CSS classes with conflict resolution.
 * Generated from UntitledUI CLI.
 * @agents generated no-review
 */

/**
 * Extended Tailwind Merge configuration with custom theme extensions.
 * Includes custom display text size classes for proper conflict resolution.
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: ['display-xs', 'display-sm', 'display-md', 'display-lg', 'display-xl', 'display-2xl'],
    },
  },
});

/**
 * Utility function for merging and deduplicating Tailwind CSS classes.
 * Automatically resolves conflicts between conflicting classes (e.g., 'px-2 px-4' becomes 'px-4').
 * Extended with custom theme support for display text sizes.
 *
 * @param classes - Class strings, arrays, or conditional expressions to merge
 * @returns Optimized class string with conflicts resolved
 *
 * @example
 * ```tsx
 * // Basic class merging
 * cx('px-2 py-1', 'px-4 bg-blue-500') // 'py-1 px-4 bg-blue-500'
 *
 * // Conditional classes
 * cx('base-class', isActive && 'active-class', 'text-sm')
 *
 * // Object-style conditions
 * cx({
 *   'text-red-500': hasError,
 *   'text-green-500': isSuccess,
 *   'text-blue-500': !hasError && !isSuccess
 * })
 *
 * // In components
 * <div className={cx(
 *   'rounded-lg p-4',
 *   size === 'lg' && 'p-6',
 *   variant === 'primary' && 'bg-blue-500 text-white'
 * )} />
 * ```
 */
export const cx = twMerge;

/**
 * Identity function for organizing style objects with Tailwind IntelliSense support.
 * Enables class sorting and autocompletion in nested style objects without runtime overhead.
 * This is a development-time utility that doesn't modify the input.
 *
 * @param classes - Style object containing class strings at any nesting level
 * @returns The exact same object unchanged
 *
 * @example
 * ```tsx
 * const styles = sortCx({
 *   button: {
 *     base: 'px-4 py-2 rounded-lg font-medium transition-colors',
 *     primary: 'bg-blue-500 text-white hover:bg-blue-600',
 *     secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300'
 *   },
 *   sizes: {
 *     sm: 'px-3 py-1.5 text-sm',
 *     md: 'px-4 py-2 text-md',
 *     lg: 'px-6 py-3 text-lg'
 *   }
 * });
 *
 * // Usage in component
 * <button className={cx(styles.button.base, styles.button.primary, styles.sizes.md)} />
 * ```
 */
export function sortCx<
  T extends Record<
    string,
    string | number | Record<string, string | number | Record<string, string | number>>
  >,
>(classes: T): T {
  return classes;
}
